import { FC } from 'react'
import { useSelector } from 'react-redux'

import { Translation } from 'cng-common/features/translations'
import { getFormattedFinance } from 'cng-common/helpers/numbers'
import { Tooltip } from 'core-components/library/tooltip'
import { calculateCockpitSobGrowth } from 'partner360/features/financialCockpit/lib/calculateCockpitGrowth'
import { selectCockpitCurrency } from 'partner360/features/financialCockpit/model'

import { FINANCIAL_COCKPIT_TRANSLATION_KEYS } from '../../../../configs/translations'
import {
  calculateCockpitGrowth,
  IExpectedExitQuarters,
  IRevenueAndSobFigures,
} from '../../../../lib'
import {
  CockpitTableColumnTitle,
  CockpitTableRowTitle,
} from '../../../components/cockpitTableTitle'
import { getDeltaTone, getYearParams } from '../../lib/metricPresentation'
import { getExpectedExitHint } from '../cockpitExpectedExitHint/getExpectedExitHint'
import { CockpitMetricCell } from '../cockpitMetricCell/CockpitMetricCell'

import styles from './CockpitMetric.module.scss'

const KEYS = FINANCIAL_COCKPIT_TRANSLATION_KEYS.revenueAndSob

interface ICockpitMetricProps {
  fYear: number
  baseline: IRevenueAndSobFigures
  plan: IRevenueAndSobFigures
  expectedExit: IRevenueAndSobFigures
  /** Which selected quarters the expected exit reads as actuals and which as plan — what the
   *  Expected Exit cell's ⓘ tooltip lists. Only read while `isPeriodOpen`. */
  expectedExitQuarters: IExpectedExitQuarters
  /** Set only while the year before `fYear` is still in its last quarter
   *  (`selectIsCockpitPrevFYearClosing`): the baseline then reads as that year's YTD, that year's
   *  expected exit follows it, and `fYear`'s own expected exit — all plan by then — is left out. */
  prevFYearExpectedExit?: {
    figures: IRevenueAndSobFigures
    quarters: IExpectedExitQuarters
  }
  actuals: IRevenueAndSobFigures
  quota: IRevenueAndSobFigures
  /** Whether the "Show quota" cockpit setting is on — gates the quota cell's visibility. */
  showQuota: boolean
  isPeriodOpen: boolean
  formatSales: (value: number | null) => string
  formatSob: (value: number | null) => string
  formatDelta: (value: number | null, isSob?: boolean) => string
}

const getGrowths = (value: IRevenueAndSobFigures, baseline: IRevenueAndSobFigures) => ({
  salesGrowth: calculateCockpitGrowth({ value: value.sales, baseline: baseline.sales }),
  sobGrowth: calculateCockpitSobGrowth({ value: value.sob, baseline: baseline.sob }),
})

export const CockpitMetric: FC<ICockpitMetricProps> = ({
  fYear,
  baseline,
  plan,
  expectedExit,
  expectedExitQuarters,
  prevFYearExpectedExit,
  actuals,
  quota,
  showQuota,
  isPeriodOpen,
  formatSales,
  formatSob,
  formatDelta,
}) => {
  const currency = useSelector(selectCockpitCurrency)
  const fYearReplaceParams = getYearParams(fYear)
  const baselineFYearReplaceParams = getYearParams(fYear - 1)

  const planGrowth = getGrowths(plan, baseline)
  const expectedExitGrowth = getGrowths(expectedExit, baseline)
  const actualsGrowth = getGrowths(actuals, baseline)
  const quotaGrowth = getGrowths(quota, baseline)

  const renderSalesValue = (sales: number | null) => (
    <Tooltip
      tipContent={() => (
        <>
          {getFormattedFinance(sales, {
            currency,
            withCurrencySign: true,
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
          })}
        </>
      )}
    >
      {formatSales(sales)}
    </Tooltip>
  )

  return (
    <div className={styles.root}>
      <div className={styles.cells}>
        <div className={styles.labels}>
          <CockpitTableColumnTitle>&nbsp;</CockpitTableColumnTitle>
          <CockpitTableRowTitle metric className={styles.title}>
            <Translation text={KEYS.sales} span />
          </CockpitTableRowTitle>
          <CockpitTableRowTitle metric className={styles.title}>
            <Translation text={KEYS.sob} span />
          </CockpitTableRowTitle>
        </div>

        <CockpitMetricCell
          label={prevFYearExpectedExit ? KEYS.ytd : KEYS.baseline}
          labelParams={baselineFYearReplaceParams}
          value={renderSalesValue(baseline.sales)}
          secondaryValue={formatSob(baseline.sob)}
        />

        {prevFYearExpectedExit && (
          <CockpitMetricCell
            label={KEYS.expectedExit}
            labelParams={baselineFYearReplaceParams}
            hint={getExpectedExitHint(prevFYearExpectedExit.quarters)}
            value={renderSalesValue(prevFYearExpectedExit.figures.sales)}
            secondaryValue={formatSob(prevFYearExpectedExit.figures.sob)}
          />
        )}

        <CockpitMetricCell
          label={KEYS.plan}
          labelParams={fYearReplaceParams}
          value={renderSalesValue(plan.sales)}
          delta={formatDelta(planGrowth.salesGrowth)}
          deltaTone={getDeltaTone(planGrowth.salesGrowth)}
          secondaryValue={formatSob(plan.sob)}
          secondaryDelta={formatDelta(planGrowth.sobGrowth, true)}
          secondaryDeltaTone={getDeltaTone(planGrowth.sobGrowth)}
        />

        {!prevFYearExpectedExit &&
          (isPeriodOpen ? (
            <CockpitMetricCell
              label={KEYS.expectedExit}
              labelParams={fYearReplaceParams}
              hint={getExpectedExitHint(expectedExitQuarters)}
              value={renderSalesValue(expectedExit.sales)}
              delta={formatDelta(expectedExitGrowth.salesGrowth)}
              deltaTone={getDeltaTone(expectedExitGrowth.salesGrowth)}
              secondaryValue={formatSob(expectedExit.sob)}
              secondaryDelta={formatDelta(expectedExitGrowth.sobGrowth, true)}
              secondaryDeltaTone={getDeltaTone(expectedExitGrowth.sobGrowth)}
            />
          ) : (
            <CockpitMetricCell
              label={KEYS.actuals}
              labelParams={fYearReplaceParams}
              value={renderSalesValue(actuals.sales)}
              delta={formatDelta(actualsGrowth.salesGrowth)}
              deltaTone={getDeltaTone(actualsGrowth.salesGrowth)}
              secondaryValue={formatSob(actuals.sob)}
              secondaryDelta={formatDelta(actualsGrowth.sobGrowth, true)}
              secondaryDeltaTone={getDeltaTone(actualsGrowth.sobGrowth)}
            />
          ))}

        {showQuota && (
          <CockpitMetricCell
            label={KEYS.quota}
            labelParams={fYearReplaceParams}
            value={renderSalesValue(quota.sales)}
            delta={formatDelta(quotaGrowth.salesGrowth)}
            deltaTone={getDeltaTone(quotaGrowth.salesGrowth)}
          />
        )}
      </div>
    </div>
  )
}
